/**
 * Student attendance API + TanStack Query hooks (Prompt 11E.6).
 *
 * GET /v1/students/{id}/attendance?period=this_week|this_month|last_n_months|custom
 *   &n_months=&from=&to=
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@core/api/client';

// Hand-declared — see the comment in ./leaves.ts for why these aren't
// sourced from the (currently stale) generated `components['schemas']`.
// Matches backend/app/features/attendance/schemas.py exactly.
export type AttendancePeriod = 'this_week' | 'this_month' | 'last_n_months' | 'custom';
export type StudentAttendanceRowStatus = 'present' | 'absent' | 'late' | 'excused' | 'makeup';

export interface StudentAttendanceRow {
  id: string;
  session_id: string;
  session_start: string | null;
  status: StudentAttendanceRowStatus;
  teacher_id: string | null;
  teacher_name: string | null;
  notes: string | null;
  remaining_reschedules: number | null;
}

export interface StudentAttendanceSummaryResponse {
  student_id: string;
  period: AttendancePeriod;
  from_date: string;
  to_date: string;
  attended: number;
  missed: number;
  excused: number;
  total: number;
  rows: StudentAttendanceRow[];
}

export interface StudentAttendanceParams {
  period: AttendancePeriod;
  nMonths?: number;
  from?: string;
  to?: string;
}

function buildQuery(params: StudentAttendanceParams): string {
  const q = new URLSearchParams({ period: params.period });
  if (params.nMonths) q.set('n_months', String(params.nMonths));
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  return q.toString();
}

export const studentAttendanceKeys = {
  summary: (studentId: string, params: StudentAttendanceParams) =>
    ['student-attendance', studentId, params.period, params.nMonths, params.from, params.to] as const,
};

export function useStudentAttendance(
  studentId: string | undefined,
  params: StudentAttendanceParams,
) {
  return useQuery({
    queryKey: studentAttendanceKeys.summary(studentId ?? '__none__', params),
    queryFn: () =>
      api.get<StudentAttendanceSummaryResponse>(
        `/v1/students/${studentId}/attendance?${buildQuery(params)}`,
      ),
    enabled: Boolean(studentId) && (params.period !== 'custom' || Boolean(params.from && params.to)),
  });
}
